import { useState } from 'react';
import { Alert } from "antd";
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Textarea,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import { applyApiFieldErrors } from '@shared/lib/forms/apply-field-errors';
import { useCreateLeadMutation } from '@entities/crm';

const schema = z.object({
  title: z.string().trim().min(1, 'required').max(256),
  buyerId: z
    .string()
    .uuid('UUID expected')
    .or(z.literal('').transform(() => undefined))
    .optional(),
  notes: z.string().max(8000).optional(),
});

type NewLeadValues = z.infer<typeof schema>;

interface NewLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewLeadDialog({ open, onOpenChange }: NewLeadDialogProps) {
  const { t } = useTranslation();
  const mutation = useCreateLeadMutation();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<NewLeadValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: '', buyerId: '', notes: '' },
  });

  const onSubmit = (values: NewLeadValues) => {
    setError(null);
    mutation.mutate(
      {
        title: values.title.trim(),
        buyerId: values.buyerId || null,
        notes: values.notes?.trim() || null,
      },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange(false);
        },
        onError: (err) => {
          if (!applyApiFieldErrors(err, form.setError)) {
            setError(err instanceof ApiError ? err.message : 'Create failed');
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('crm.newDialog.title')}</DialogTitle>
          <DialogDescription>{t('crm.newDialog.description')}</DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <FormField label={t('crm.newDialog.leadTitle')} required error={form.formState.errors.title?.message}>
              {(p) => <Input {...p} {...form.register('title')} />}
            </FormField>

            <FormField
              label={t('crm.newDialog.buyerId')}
              hint={t('crm.newDialog.buyerIdHint')}
              error={form.formState.errors.buyerId?.message}
            >
              {(p) => <Input {...p} {...form.register('buyerId')} />}
            </FormField>

            <FormField label={t('crm.newDialog.notes')} error={form.formState.errors.notes?.message}>
              {(p) => <Textarea {...p} rows={4} {...form.register('notes')} />}
            </FormField>

            {error ? <Alert type="error" showIcon message={error} /> : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={mutation.isPending}>
                {t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
